import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { STATS, SERVICES, TESTIMONIALS } from "@/data/index";
import { springPresets } from "@/lib/motion";

/* @section: home-hero */
function Hero() {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-background">
      {/* Hero background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/gen/hero_bg_2.png"
          alt="企業研修"
          className="w-full h-full object-cover"
          style={{ opacity: 0.42 }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--background) 55%, transparent 100%)" }} />
      </div>
      {/* Background accent */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 60% 40%, color-mix(in srgb, var(--primary) 6%, transparent) 0%, transparent 70%)",
        }}
      />
      <div className="container mx-auto px-6 py-28 relative z-10">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
          >
            <span
              className="inline-block text-xs text-primary tracking-[0.3em] uppercase mb-8 border-b border-primary pb-1"
            >
              Corporate Training & Consulting
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-semibold text-foreground leading-[1.15] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: "-0.01em" }}
          >
            組織行動論で、<br />
            <span className="text-primary">人と組織の</span><br />
            実行力を高める。
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.35 }}
            className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-xl"
          >
            HR Progress Coaching & Consultingは、組織行動論をベースにした
            企業研修・組織開発コンサルティングで、
            リーダーと組織の実行力を高めるサポートをしています。
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.45 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm"
              style={{ letterSpacing: "0.1em" }}
            >
              サービスを見る
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-border text-foreground text-sm tracking-wider hover:bg-muted/50 transition-colors rounded-sm"
              style={{ letterSpacing: "0.1em" }}
            >
              お問い合わせ
            </Link>
          </motion.div>
        </div>
      </div>
      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-muted-foreground"
      >
        <span className="text-xs tracking-widest" style={{ letterSpacing: "0.2em" }}>SCROLL</span>
        <ChevronDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

/* @section: home-concept */
function Concept() {
  return (
    <section className="py-28 bg-background">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <span className="text-xs text-primary tracking-[0.25em] uppercase mb-4 block" style={{ letterSpacing: "0.25em" }}>
              Our Philosophy
            </span>
            <h2
              className="text-3xl md:text-4xl font-semibold text-foreground leading-[1.4] mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              組織行動論が、<br />
              最速の組織変革への鍵。
            </h2>
            <div className="w-12 h-px bg-primary mb-6" />
            <p className="text-muted-foreground leading-[1.9] mb-5">
              組織行動論とは、人・チーム・組織がどのように機能するかを科学的に分析する学問です。
              この知見をベースにすることで、感覚論ではなく再現性のあるリーダーシップ開発と組織改革が実現します。
            </p>
            <p className="text-muted-foreground leading-[1.9]">
              HR Progress Coaching & Consultingは、MBAで培った組織行動論の理論と
              実務現場での経験を掛け合わせ、「現場で使える」研修・コンサルティングを提供しています。
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="relative"
          >
            <div className="rounded-sm overflow-hidden" style={{ boxShadow: "0 8px 40px -8px color-mix(in srgb, var(--primary) 15%, transparent)" }}>
              <img src="/images/gen/concept_img_2.png" alt="ビジネス研修" className="w-full h-72 object-cover" />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {STATS.map((stat, i) => (
                <div key={i} className="bg-muted/40 border border-border rounded-sm p-4 text-center">
                  <div className="text-2xl font-bold text-primary mb-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{stat.value}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* @section: home-services */
function ServicesPreview() {
  return (
    <section className="py-28 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-xs text-primary tracking-[0.25em] uppercase mb-4 block">Services</span>
          <h2
            className="text-3xl md:text-4xl font-semibold text-foreground"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            提供サービス
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.slice(0, 4).map((service, i) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.1 }}
              className="bg-background border border-border p-8 rounded-sm hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-xs text-primary tracking-wider">{service.subtitle}</span>
                  <h3
                    className="text-xl font-semibold text-foreground mt-1"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    {service.title}
                  </h3>
                </div>
                <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all mt-1" />
              </div>
              <div className="w-8 h-px bg-primary mb-4" />
              <p className="text-sm text-muted-foreground leading-[1.8]">{service.description}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-primary tracking-wider hover:gap-3 transition-all"
            style={{ letterSpacing: "0.1em" }}
          >
            すべてのサービスを見る
            <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* @section: home-testimonials */
function Testimonials() {
  return (
    <section className="py-28 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-xs text-primary tracking-[0.25em] uppercase mb-4 block">Voices</span>
          <h2
            className="text-3xl md:text-4xl font-semibold text-foreground"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            受講者の声
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.12 }}
              className="border border-border p-8 rounded-sm bg-background"
            >
              <div className="text-3xl text-primary mb-4" style={{ fontFamily: "Georgia, serif", lineHeight: 1 }}>"</div>
              <p className="text-sm text-muted-foreground leading-[1.9] mb-6">{t.text}</p>
              <div className="border-t border-border pt-4">
                <div className="text-sm font-medium text-foreground">{t.author}</div>
                <div className="text-xs text-muted-foreground mt-1">{t.company}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* @section: home-cta */
function CTA() {
  return (
    <section className="py-28 bg-foreground">
      <div className="container mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.gentle}
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block" style={{ color: "color-mix(in srgb, var(--primary) 80%, white)" }}>
            Contact
          </span>
          <h2
            className="text-3xl md:text-4xl font-semibold leading-[1.5] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif", color: "var(--background)" }}
          >
            まずは、お気軽にご相談ください。
          </h2>
          <p className="text-sm leading-relaxed mb-10 max-w-lg mx-auto" style={{ color: "color-mix(in srgb, var(--background) 70%, transparent)" }}>
            コーチングや研修のご相談、お見積りなど、何でもお気軽にどうぞ。
            初回はご相談無料で対応しています。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-4 text-sm tracking-wider hover:gap-4 transition-all rounded-sm"
            style={{
              background: "var(--background)",
              color: "var(--foreground)",
              letterSpacing: "0.12em",
            }}
          >
            お問い合わせはこちら
            <ArrowRight size={15} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Concept />
      <ServicesPreview />
      <Testimonials />
      <CTA />
    </>
  );
}
